import Vue, { VNode } from 'vue';
import { i18n } from 'i18next';

import { ThemeType } from '@/HostApp/types/ThemeType';

import { loadScript } from '@/Common/Runtime/host';
import {
	serverProps,
	bootstrapCssUrl,
	ux1FrameworkCssUrl,
	defaultCssUrls,
	tc2VariablesCssUrl,
	tc2CssUrls
} from '@/Common/Environment/const';

import aps from '@/HostApp/store/ApsModules/ApsModule';
import hostServices from '@/HostApp/services';

import App from '@/HostApp/ui/App.vue';

const { designTokensModule, cssBundlerModule, spriteBundlerModule, i18nServiceModule, fontLoaderModule } = hostServices;

export class HostApp {
	public vm: Vue | null = null;
	public apsHost!: Promise<void | Array<Event>>;
	protected i18nLoaded: null | Promise<void | i18n> = null;

	constructor() {
		const { locale } = serverProps;

		aps.hostServices = hostServices;
		this.i18nLoaded = i18nServiceModule.init(locale).then(() => {
			return i18nServiceModule.loadDictionary();
		});

		fontLoaderModule.loadAllFonts();
		spriteBundlerModule.prefetchSprites();

		const cssToPrefetch = this.getCssToPrefetch();
		cssBundlerModule.prefetchStyleSheets(cssToPrefetch);

		this.apsHost = this.loadAps();

		aps.notifications.runService().catch((e) => {
			// eslint-disable-next-line no-console
			console.warn('cannot start notification service: ', e);
		});
	}

	public get isNewExperience(): boolean {
		return serverProps._vueWorkspace;
	}

	public getCssToPrefetch(): Array<string> {
		// Can't use ThemeType, cause branding doesn't return difference between TC1 and TC2 skins in serverProps,
		// only 'serverProps.isDefaultSkin' to determine default or custom theme.
		if (!serverProps.isDefaultSkin && (this.isNewExperience || serverProps.isAnonymousContext)) {
			return [...defaultCssUrls, ux1FrameworkCssUrl, bootstrapCssUrl, ...tc2CssUrls];
		} else {
			// no-op
		}
		return [...defaultCssUrls, ux1FrameworkCssUrl, bootstrapCssUrl];
	}

	public async loadAps(): Promise<void | Array<Event>> {
		const [hostLayerSrc, apsSrc] = serverProps.devMode
			? ['/aps/2/ui/runtime/host/hostLayer.debug.js?', '/aps/2/ui/runtime/client/aps/aps.debug.js?']
			: [
					`/aps/2/ui/runtime/host/hostLayer.js?apsUIRuntimeVersion=${serverProps.uiRuntimeVersion}`,
					`/aps/2/ui/runtime/client/aps/aps.js?apsUIRuntimeVersion=${serverProps.uiRuntimeVersion}`
			  ];
		await loadScript(apsSrc);
		return loadScript(hostLayerSrc);
	}

	public async renderViewport(): Promise<void> {
		await this.onBeforeStart();
		this.vm = new Vue({
			el: '#app',
			render: (h): VNode => h(App)
		});
	}

	protected async onBeforeStart(): Promise<void> {
		const themeType = await this.computeThemeType();
		await this.fetchDictionaryAndApplyCss(themeType);
		await this.setThemeCssVariables(themeType);
	}

	protected async computeThemeType(): Promise<ThemeType> {
		if (serverProps.isDefaultSkin) {
			designTokensModule.setThemeType(ThemeType.Default);
		} else {
			const tc2VariablesCss = await cssBundlerModule.getCSSStyleSheetByUrl(tc2VariablesCssUrl);
			const hasTc2VariablesCss = tc2VariablesCss?.cssRules.length;

			if (hasTc2VariablesCss && (this.isNewExperience || serverProps.isAnonymousContext)) {
				designTokensModule.setThemeType(ThemeType.ThemeCreator2);
			} else {
				designTokensModule.setThemeType(ThemeType.ThemeCreator1);
			}
		}
		return designTokensModule.themeType;
	}

	protected async fetchDictionaryAndApplyCss(themeType: ThemeType): Promise<void> {
		const cssUrlsToApply = this.getCssUrlsToApply(themeType);
		const promises: (Promise<void | CSSStyleSheet | i18n> | undefined | null)[] = [
			cssBundlerModule.applyCSSBundles(document, cssUrlsToApply),
			spriteBundlerModule.attachSprite(document.body as HTMLBodyElement),
			this.i18nLoaded
		];

		await Promise.all(promises);
	}

	protected getCssUrlsToApply(themeType: ThemeType): Array<string> {
		if (this.isNewExperience) {
			if (themeType === ThemeType.ThemeCreator2) return [...defaultCssUrls, ux1FrameworkCssUrl, ...tc2CssUrls];
			else return [...defaultCssUrls, ux1FrameworkCssUrl];
		} else {
			if (themeType === ThemeType.ThemeCreator2) return [ux1FrameworkCssUrl, ...tc2CssUrls, bootstrapCssUrl];
			return [ux1FrameworkCssUrl, bootstrapCssUrl];
		}
	}

	protected async setThemeCssVariables(themeType: ThemeType): Promise<void> {
		// All Promises already fulfilled at this moment.
		// Await's are necessary to get results from them.
		const ux1FrameworkCss = await cssBundlerModule.getCSSStyleSheetByUrl(ux1FrameworkCssUrl);

		if (ux1FrameworkCss) {
			designTokensModule.setUx1FrameworkCss(ux1FrameworkCss);
		}

		if (themeType === ThemeType.ThemeCreator1) {
			const bootstrapCss = await cssBundlerModule.getCSSStyleSheetByUrl(bootstrapCssUrl);
			if (bootstrapCss) {
				designTokensModule.mergeVariablesWithTheme(bootstrapCss);
			}
		} else if (themeType === ThemeType.ThemeCreator2) {
			const tc2VariablesCss = await cssBundlerModule.getCSSStyleSheetByUrl(tc2VariablesCssUrl);
			if (tc2VariablesCss) {
				designTokensModule.setThemeVariables(tc2VariablesCss);
			}
		} else {
			// no-op
		}
	}
}

const hostApp = new HostApp();
export default hostApp;
